/**
 * Bind the refresh-banner store to a QueryClient.
 *
 * Subscribes to both the mutation cache (writes that fail with a row-version
 * conflict) and the query cache (reads — rare, but a feed/list could
 * surface a conflict from a contained mutation). Every error is examined
 * via `isRowVersionConflictError`; if true, raise() on the store.
 *
 * Called once at app init, after the QueryClient is constructed.
 */

import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api-client';
import { raise } from './refresh-banner-store';

/**
 * Predicate: is this thrown value a row-version conflict ApiError?
 *
 * Exported and pure so it can be unit-tested without spinning up a
 * QueryClient. The attach function below uses this to filter cache
 * events.
 */
export function isRowVersionConflictError(value: unknown): boolean {
  return value instanceof ApiError && value.isRowVersionConflict();
}

export function attachRefreshBanner(qc: QueryClient): () => void {
  const mutationUnsub = qc.getMutationCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') {
      if (isRowVersionConflictError(event.action.error)) {
        raise();
      }
    }
  });

  const queryUnsub = qc.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') {
      if (isRowVersionConflictError(event.action.error)) {
        raise();
      }
    }
  });

  return () => {
    mutationUnsub();
    queryUnsub();
  };
}
